import styled from 'styled-components';

/**
 * Positioning wrapper around the lib `<Sheet>` — pins the run history
 * surface to cover the entire canvas slot. The lib Sheet is chrome
 * only (background, flex column, animation) so positioning lives
 * here. `z-index: 6` keeps the sheet above the React Flow node /
 * edge layer.
 */
export const SheetPositionWrap = styled.div`
  position: absolute;
  inset: 0;
  z-index: 6;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

/* Vertical list of row cards, 4px gap matching the design. */
export const RunRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

/* Outer wrap per row — flex container holding the card. */
export const RowWrap = styled.div`
  display: flex;
  align-items: stretch;
`;

/* The card itself — flex: 1 so it fills the wrap. */
export const RowCard = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  cursor: pointer;
`;

/* Single-row table inside each card. table-layout: fixed so the
   middle cell clips + ellipsizes. border-spacing: 0 so spacing comes
   from the RunRows gap. */
export const RowTable = styled.table`
  width: 100%;
  border-spacing: 0;
  table-layout: fixed;

  & tbody tr {
    background: ${({ theme: { colors = {} } }) =>
      colors.surfaceContainerLow ?? '#13141E'};
    cursor: pointer;
    transition: background 140ms ease, box-shadow 140ms ease;
  }

  & td {
    padding: 14px 12px;
    vertical-align: middle;
    box-shadow: inset 0 1px 0 transparent, inset 0 -1px 0 transparent;
    transition: box-shadow 140ms ease;
  }
  & td:first-child {
    border-top-left-radius: 24px;
    border-bottom-left-radius: 24px;
  }
  & td:last-child {
    border-top-right-radius: 24px;
    border-bottom-right-radius: 24px;
  }

  & tbody tr:hover {
    background: ${({ theme: { colors = {} } }) =>
      colors.surfaceContainer ?? '#1D1B26'};
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.18);
  }
  & tbody tr:hover td {
    box-shadow: inset 0 1px 0 #676680, inset 0 -1px 0 #676680;
  }
  & tbody tr:hover td:first-child {
    box-shadow: inset 1px 0 0 #676680, inset 0 1px 0 #676680,
      inset 0 -1px 0 #676680;
  }
  & tbody tr:hover td:last-child {
    box-shadow: inset -1px 0 0 #676680, inset 0 1px 0 #676680,
      inset 0 -1px 0 #676680;
  }
`;

/* Leading cell — fixed 72px, holds the run number. */
export const IdCell = styled.td`
  width: 72px;
  vertical-align: middle;
`;

export const RunId = styled.span`
  font-size: 13px;
  font-weight: 600;
  line-height: 1;
  color: ${({ theme: { colors = {} } }) => colors.onSurface ?? '#E6E6F2'};
`;

/* Middle cell — name block (stop btn/spacer + title + meta). */
export const NameCell = styled.td`
  vertical-align: middle;
`;

/* p-table__name — flex row: icon/stop slot + text block. */
export const NameBlock = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

/* Stop button (40×40 circle, red icon). */
export const StopButton = styled.button`
  width: 40px;
  height: 40px;
  flex: 0 0 40px;
  border-radius: 50%;
  border: none;
  background: transparent;
  color: #ef4444;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 120ms ease;

  &:hover {
    background: color-mix(in oklch, #f87171 18%, transparent);
  }

  & > svg {
    width: 28px;
    height: 28px;
  }
`;

/* Invisible spacer — same 40×40 footprint as the stop button so
   non-running rows align with running ones. */
export const StopSpacer = styled.span`
  width: 40px;
  height: 40px;
  flex: 0 0 40px;
`;

/* Text block next to the stop/spacer. */
export const NameText = styled.div`
  min-width: 0;
  flex: 1;
`;

/* Title line — 13.5px / 600, run input filename + duration inline. */
export const NameTitle = styled.div`
  font-size: 13.5px;
  font-weight: 600;
  color: ${({ theme: { colors = {} } }) => colors.onSurface ?? '#E6E6F2'};
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/* Duration shown inline after the filename — mono 11.5px. */
export const DurationInline = styled.span`
  margin-left: 10px;
  font-size: 11.5px;
  font-variant-numeric: tabular-nums;
  color: ${({ theme: { colors = {} } }) =>
    colors.onSurfaceVariant ?? '#A1A3B8'};
`;

/* Meta line — 11.5px, owner + time + notes. */
export const MetaLine = styled.div`
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 4px;
  font-size: 11.5px;
  color: ${({ theme: { colors = {} } }) =>
    colors.onSurfaceVariant ?? '#A1A3B8'};
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

/* Owner chip — avatar + name, inline. */
export const MetaOwner = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: ${({ theme: { colors = {} } }) =>
    colors.onSurfaceVariant ?? '#A1A3B8'};
`;

/* Middot separator between meta items. */
export const MetaSep = styled.span`
  color: ${({ theme: { colors = {} } }) =>
    colors.onSurfaceVariant ?? '#A1A3B8'};
  opacity: 0.5;
`;

/* Mono-styled meta fragment (time). */
export const MetaMono = styled.span`
  font-size: 11px;
  color: ${({ theme: { colors = {} } }) =>
    colors.onSurfaceVariant ?? '#A1A3B8'};
`;

/* Notes — 11.5px, same color as meta. */
export const MetaNote = styled.span`
  font-size: 11.5px;
  color: ${({ theme: { colors = {} } }) =>
    colors.onSurfaceVariant ?? '#A1A3B8'};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/* Trailing cell — 180px, right-aligned, status + kebab. */
export const TrailingCell = styled.td`
  width: 180px;
  white-space: nowrap;
  text-align: right;
  vertical-align: middle;
`;

/* Actions row — pill + kebab, end-aligned, 10px gap. */
export const RowActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  justify-content: flex-end;
`;

/* Status pill — colored label with leading dot/spinner, no background. */
export const StatusPill = styled.span<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  color: ${(p) => p.$color};
`;

export const StatusDot = styled.span<{ $color: string }>`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: currentColor;
  flex: 0 0 auto;
  color: ${(p) => p.$color};
`;

/**
 * Centred wrapper used for the loading-spinner placeholder in the sheet
 * body. The actual empty state (icon + message) uses the lib `<EmptyState>`
 * atom — this wrapper only exists so the spinner gets the same vertical
 * gutter as the empty state without sprouting an `EmptyState size="md"`
 * with a spinner shoved into the `title` slot.
 */
export const LoadingWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
`;
